import React, { ReactNode, forwardRef, useImperativeHandle, useState } from 'react';

import { Button, Modal } from 'antd';

interface CommonDialogProps {
  trigger?: ReactNode;
  title?: ReactNode;
  width?: number | string;
  children: ReactNode;
  onCancel?: () => void;
  destroyOnClose?: boolean;
  maskClosable?: boolean;
  keyboard?: boolean;
  centered?: boolean;
  cancelButton?: boolean;
}

interface CommonDialogRef {
  closeDialog: () => void;
  openDialog: () => void;
}

interface FooterProps {
  children: ReactNode;
}

const CommonDialog = forwardRef<CommonDialogRef, CommonDialogProps>(
  (
    {
      trigger,
      title,
      width = 720,
      children,
      onCancel,
      destroyOnClose = true,
      maskClosable = false,
      keyboard = true,
      centered = true,
      cancelButton = true,
    },
    ref
  ) => {
    const [open, setOpen] = useState(false);

    const handleOpen = () => {
      setOpen(true);
    };

    const handleCancel = () => {
      setOpen(false);
      onCancel?.();
    };

    useImperativeHandle(ref, () => ({
      closeDialog: handleCancel,
      openDialog: handleOpen,
    }));

    const childrenArray = React.Children.toArray(children);
    const footerComponent = childrenArray.find(
      (child) =>
        React.isValidElement(child) &&
        (child.type === Footer || (child.type as any)?.displayName === 'Footer')
    );
    const otherComponents = childrenArray.filter(
      (child) =>
        !React.isValidElement(child) ||
        (child.type !== Footer && (child.type as any)?.displayName !== 'Footer')
    );

    return (
      <>
        {trigger && <div onClick={handleOpen}>{trigger}</div>}
        <Modal
          className="max-h-[90vh] overflow-y-auto"
          title={title}
          open={open}
          width={width}
          onCancel={handleCancel}
          footer={null}
          destroyOnClose={destroyOnClose}
          maskClosable={maskClosable}
          keyboard={keyboard}
          centered={centered}
        >
          <div className="max-h-[80vh] overflow-y-auto">
            {otherComponents}
            {footerComponent ||
              (cancelButton && (
                <div style={{ marginTop: 24, textAlign: 'right' }}>
                  <Button onClick={handleCancel}>Cancel</Button>
                </div>
              ))}
          </div>
        </Modal>
      </>
    );
  }
);

const Footer: React.FC<FooterProps> = ({ children }) => children;

Footer.displayName = 'Footer';
CommonDialog.displayName = 'CommonDialog';

// Extend the component with Footer property
interface CommonDialogComponent
  extends React.ForwardRefExoticComponent<
    CommonDialogProps & React.RefAttributes<CommonDialogRef>
  > {
  Footer: typeof Footer;
}

(CommonDialog as CommonDialogComponent).Footer = Footer;

export default CommonDialog as CommonDialogComponent;
